import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Slider } from '@/components/ui/slider';
import {
  Radio, ArrowRight, Monitor, DollarSign, Shield, Zap, Users, BarChart3,
  Play, TrendingUp, Globe, CheckCircle, LogIn, X, ChevronRight,
  Eye, Signal, Tv, Lock, Unlock, Volume2, VolumeX
} from 'lucide-react';

// ── Animated Counter ──
function Counter({ end, duration = 2000, prefix = '', suffix = '', decimals = 0 }) {
  const [val, setVal] = useState(0);
  const ref = useRef(null);
  const hasAnimated = useRef(false);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !hasAnimated.current) {
        hasAnimated.current = true;
        const num = parseFloat(end) || 0;
        let start = 0;
        const inc = num / (duration / 16);
        const timer = setInterval(() => {
          start += inc;
          if (start >= num) { setVal(num); clearInterval(timer); }
          else setVal(start);
        }, 16);
      }
    }, { threshold: 0.3 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [end, duration]);
  return <span ref={ref}>{prefix}{val.toFixed(decimals)}{suffix}</span>;
}

// ── Live Ticker ──
function LiveTicker() {
  const [ticks, setTicks] = useState([
    { streamer: 'xFrostTV', ccv: '12,400', cpm: '$18.50', platform: 'Twitch' },
    { streamer: 'NightOwlGG', ccv: '3,200', cpm: '$9.00', platform: 'YouTube' },
    { streamer: 'PixelQueen', ccv: '8,700', cpm: '$14.25', platform: 'Kick' },
    { streamer: 'VortexLive', ccv: '1,800', cpm: '$6.50', platform: 'Facebook' },
    { streamer: 'StreamKingX', ccv: '22,100', cpm: '$24.00', platform: 'Twitch' },
  ]);
  return (
    <div className="overflow-hidden relative">
      <div className="flex animate-scroll gap-8" style={{ animation: 'scroll 20s linear infinite' }}>
        {[...ticks, ...ticks].map((t, i) => (
          <div key={i} className="flex items-center gap-4 px-5 py-2.5 border border-white/5 rounded bg-white/[0.02] whitespace-nowrap flex-shrink-0">
            <div className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
            <span className="text-sm text-white font-medium">{t.streamer}</span>
            <span className="text-xs text-gray-500 font-mono">{t.platform}</span>
            <span className="text-xs text-emerald-400 font-mono font-bold">{t.cpm}</span>
            <span className="text-xs text-gray-600 font-mono">{t.ccv} CCV</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Earnings Calculator ──
function EarningsCalculator() {
  const [ccv, setCcv] = useState(2000);
  const [breaksPerStream, setBreaksPerStream] = useState(3);
  const [streamsPerWeek, setStreamsPerWeek] = useState(5);

  const getCpmForCcv = (v) => {
    if (v >= 10000) return 25;
    if (v >= 2000) return 15;
    if (v >= 500) return 8;
    return 3;
  };
  const getTier = (v) => {
    if (v >= 10000) return 'Elite';
    if (v >= 2000) return 'Premium';
    if (v >= 500) return 'Rising';
    return 'Emerging';
  };

  const cpm = getCpmForCcv(ccv);
  const perBreak = (ccv * cpm) / 1000 * 0.7;
  const perStream = perBreak * breaksPerStream;
  const perWeek = perStream * streamsPerWeek;
  const perMonth = perWeek * 4;

  return (
    <div className="border border-white/10 rounded-xl bg-[#0a0a0a] p-8">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-3 h-3 bg-emerald-400 rounded-full animate-pulse" />
        <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest">Live Calculator</span>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* Inputs */}
        <div className="space-y-8">
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Average CCV</label>
              <span className="text-lg font-bold text-white font-mono">{ccv.toLocaleString()}</span>
            </div>
            <Slider data-testid="calc-ccv-slider" value={[ccv]} onValueChange={v => setCcv(v[0])} min={100} max={50000} step={100}
              className="[&_[role=slider]]:bg-[#FF3B30] [&_[role=slider]]:border-0 [&_[role=slider]]:w-5 [&_[role=slider]]:h-5" />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-600 font-mono">100</span>
              <span className="text-xs text-gray-600 font-mono">50,000</span>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Ad Breaks / Stream</label>
              <span className="text-lg font-bold text-white font-mono">{breaksPerStream}</span>
            </div>
            <Slider data-testid="calc-breaks-slider" value={[breaksPerStream]} onValueChange={v => setBreaksPerStream(v[0])} min={1} max={10} step={1}
              className="[&_[role=slider]]:bg-[#7C3AED] [&_[role=slider]]:border-0 [&_[role=slider]]:w-5 [&_[role=slider]]:h-5" />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-xs font-mono text-gray-500 uppercase tracking-widest">Streams / Week</label>
              <span className="text-lg font-bold text-white font-mono">{streamsPerWeek}</span>
            </div>
            <Slider data-testid="calc-streams-slider" value={[streamsPerWeek]} onValueChange={v => setStreamsPerWeek(v[0])} min={1} max={7} step={1}
              className="[&_[role=slider]]:bg-[#7C3AED] [&_[role=slider]]:border-0 [&_[role=slider]]:w-5 [&_[role=slider]]:h-5" />
          </div>
        </div>

        {/* Output */}
        <div>
          <div className="border border-white/10 rounded-lg bg-[#050505] p-6 mb-4">
            <p className="text-xs font-mono text-gray-500 uppercase tracking-widest mb-1">Your CPM Tier</p>
            <div className="flex items-center gap-3">
              <span className="text-3xl font-black text-[#FF3B30] font-mono">${cpm}</span>
              <span className="text-sm text-gray-400 font-heading font-bold uppercase">{getTier(ccv)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="border border-white/10 rounded-lg bg-[#050505] p-4">
              <p className="text-xs font-mono text-gray-500 uppercase">Per Stream</p>
              <p className="text-xl font-black text-white font-mono">${perStream.toFixed(0)}</p>
            </div>
            <div className="border border-white/10 rounded-lg bg-[#050505] p-4">
              <p className="text-xs font-mono text-gray-500 uppercase">Per Week</p>
              <p className="text-xl font-black text-white font-mono">${perWeek.toFixed(0)}</p>
            </div>
          </div>

          <div className="border border-[#FF3B30]/30 rounded-lg bg-[#FF3B30]/[0.03] p-6 text-center">
            <p className="text-xs font-mono text-[#FF3B30] uppercase tracking-widest mb-2">Estimated Monthly Earnings</p>
            <p className="text-5xl font-black text-white font-heading">${perMonth.toLocaleString('en-US', { maximumFractionDigits: 0 })}</p>
            <p className="text-xs text-gray-500 font-mono mt-2">After 70% revenue share</p>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Comparison Table ──
function ComparisonSection() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Old Way */}
      <div className="border border-white/10 rounded-xl bg-[#0a0a0a] p-8 relative opacity-60">
        <div className="absolute top-4 right-4">
          <span className="text-xs font-mono text-red-400 uppercase tracking-widest border border-red-500/20 bg-red-500/5 px-2 py-1 rounded">Old Way</span>
        </div>
        <h3 className="font-heading text-xl font-bold text-white mb-6 flex items-center gap-2">
          <X className="w-5 h-5 text-red-400" /> Overlay-Based Ads
        </h3>
        <div className="space-y-4">
          {[
            { icon: Lock, text: 'Blocked by 40%+ of viewers using ad blockers', bad: true },
            { icon: Tv, text: 'Single platform only — Twitch OR YouTube, never both', bad: true },
            { icon: DollarSign, text: '$1-3 CPM — race to the bottom pricing', bad: true },
            { icon: Eye, text: 'Viewers ignore overlay banners — banner blindness', bad: true },
            { icon: VolumeX, text: 'No audio — silent overlays get zero engagement', bad: true },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <item.icon className="w-4 h-4 text-red-400/60 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-500 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* AYO Way */}
      <div className="border border-emerald-500/20 rounded-xl bg-[#0a0a0a] p-8 relative">
        <div className="absolute top-4 right-4">
          <span className="text-xs font-mono text-emerald-400 uppercase tracking-widest border border-emerald-500/20 bg-emerald-500/5 px-2 py-1 rounded flex items-center gap-1">
            <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full animate-pulse" /> AYO
          </span>
        </div>
        <h3 className="font-heading text-xl font-bold text-white mb-6 flex items-center gap-2">
          <CheckCircle className="w-5 h-5 text-emerald-400" /> Source-Level Delivery
        </h3>
        <div className="space-y-4">
          {[
            { icon: Unlock, text: '100% view rate — ads are part of the stream, not on top of it' },
            { icon: Globe, text: 'Every platform simultaneously — Twitch, YouTube, Kick, Facebook' },
            { icon: DollarSign, text: '$3-25+ CPM — premium pricing for premium delivery' },
            { icon: Eye, text: 'Full-screen takeover — impossible to ignore' },
            { icon: Volume2, text: 'Full audio + video — TV-quality ad experience' },
          ].map((item, i) => (
            <div key={i} className="flex items-start gap-3">
              <item.icon className="w-4 h-4 text-emerald-400 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-gray-300 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Main Landing Page ──
export default function LandingPage() {
  const [activeTab, setActiveTab] = useState('brands');

  return (
    <div className="min-h-screen bg-[#050505] overflow-x-hidden">
      <style>{`
        @keyframes scroll { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        @keyframes float { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-8px); } }
        .animate-scroll { animation: scroll 25s linear infinite; }
        .animate-float { animation: float 6s ease-in-out infinite; }
      `}</style>

      {/* NAV */}
      <nav className="fixed top-0 left-0 right-0 z-50 border-b border-white/[0.04] bg-[#050505]/90 backdrop-blur-2xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-[#FF3B30] flex items-center justify-center"><Radio className="w-4 h-4 text-white" /></div>
            <span className="font-heading text-xl font-black tracking-tighter text-white uppercase">AYO<span className="text-gray-500 ml-1 font-medium text-sm normal-case">source</span></span>
          </div>
          <div className="hidden md:flex items-center gap-8">
            <a href="#difference" className="text-sm text-gray-500 hover:text-white transition-colors">The Difference</a>
            <a href="#calculator" className="text-sm text-gray-500 hover:text-white transition-colors">Calculator</a>
            <a href="#how" className="text-sm text-gray-500 hover:text-white transition-colors">How It Works</a>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" className="text-gray-400 hover:text-white hover:bg-white/5 text-sm"><LogIn className="w-4 h-4 mr-1.5" /> Sign In</Button></Link>
            <Link to="/signup/brand"><Button className="bg-white/5 hover:bg-white/10 border border-white/10 text-white text-sm px-4 h-9">Get Started</Button></Link>
          </div>
        </div>
      </nav>

      {/* HERO — Asymmetric Layout */}
      <section className="relative pt-28 pb-8 px-6">
        <div className="absolute top-0 left-0 right-0 h-[600px] bg-gradient-to-b from-[#FF3B30]/[0.02] to-transparent pointer-events-none" />
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center relative z-10">
          {/* Left: Copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 border border-[#FF3B30]/20 rounded-full bg-[#FF3B30]/[0.03] mb-6">
              <div className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
              <span className="text-[11px] font-mono text-[#FF3B30]/80 uppercase tracking-[0.2em]">The CTV Ad Network for Live Streaming</span>
            </div>

            <h1 className="font-heading text-5xl sm:text-6xl lg:text-[4.5rem] font-black tracking-[-0.04em] text-white uppercase leading-[0.88] mb-6">
              Source-Level<br />
              Ads For<br />
              <span className="text-[#FF3B30]">Live Streams</span>
            </h1>

            <p className="text-base text-gray-400 max-w-md leading-relaxed mb-10">
              Deliver ads directly through OBS — not overlays. Unblockable, multi-platform, premium CPMs. 
              The way streaming ads should have always worked.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 mb-10">
              <Link to="/signup/brand" data-testid="hero-cta-brand">
                <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold uppercase tracking-widest py-5 px-8 rounded-none text-xs shadow-[0_0_30px_rgba(124,58,237,0.15)] hover:shadow-[0_0_50px_rgba(124,58,237,0.3)] transition-all h-auto">
                  <BarChart3 className="w-4 h-4 mr-2" /> I'm a Brand <ArrowRight className="w-4 h-4 ml-3" />
                </Button>
              </Link>
              <Link to="/signup/creator" data-testid="hero-cta-creator">
                <Button className="bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold uppercase tracking-widest py-5 px-8 rounded-none text-xs shadow-[0_0_30px_rgba(255,59,48,0.15)] hover:shadow-[0_0_50px_rgba(255,59,48,0.3)] transition-all h-auto">
                  <Play className="w-4 h-4 mr-2" /> I'm a Creator <ArrowRight className="w-4 h-4 ml-3" />
                </Button>
              </Link>
            </div>

            {/* Mini Stats */}
            <div className="flex gap-8">
              {[
                { val: '70', suffix: '%', label: 'Revenue Share' },
                { val: '<1', suffix: 's', label: 'Trigger Latency' },
                { val: '100', suffix: '%', label: 'View Rate' },
              ].map((s, i) => (
                <div key={i}>
                  <p className="text-2xl font-black text-white font-mono">{s.val}{s.suffix}</p>
                  <p className="text-[10px] text-gray-600 font-mono uppercase tracking-widest">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Right: Dashboard Mock */}
          <div className="relative animate-float hidden lg:block">
            <div className="relative rounded-xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50">
              <img
                src="https://static.prod-images.emergentagent.com/jobs/34cab473-7882-4a8a-abac-c36cae9e6ad7/images/1e78eac190ee1a4cb27e32f84978e88fdb9dfcf8c2a03a61601ba0284e35ff51.png"
                alt="AYO Dashboard"
                className="w-full"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-transparent opacity-40" />
            </div>
            {/* Floating badge */}
            <div className="absolute -bottom-4 -left-4 bg-[#121212] border border-white/10 rounded-lg px-4 py-3 shadow-xl">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#FF3B30] rounded-full animate-pulse" />
                <span className="text-xs font-mono text-white">LIVE</span>
                <span className="text-xs font-mono text-gray-500">12.4k viewers</span>
              </div>
            </div>
            <div className="absolute -top-3 -right-3 bg-[#121212] border border-emerald-500/20 rounded-lg px-3 py-2 shadow-xl">
              <p className="text-xs font-mono text-emerald-400">+$18.50 CPM</p>
            </div>
          </div>
        </div>
      </section>

      {/* LIVE TICKER */}
      <section className="border-y border-white/5 bg-[#0a0a0a] py-4 mt-8">
        <LiveTicker />
      </section>

      {/* PLATFORM STRIP */}
      <section className="max-w-5xl mx-auto px-6 py-14 text-center">
        <p className="text-xs font-mono text-gray-600 uppercase tracking-[0.3em] mb-8">Delivering ads across</p>
        <div className="flex items-center justify-center gap-12 flex-wrap opacity-40">
          {['TWITCH', 'YOUTUBE', 'KICK', 'FACEBOOK LIVE', 'TIKTOK LIVE', 'RUMBLE'].map(p => (
            <span key={p} className="text-sm font-heading font-bold text-gray-400 tracking-wider">{p}</span>
          ))}
        </div>
      </section>

      {/* THE DIFFERENCE */}
      <section id="difference" className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-left mb-12">
          <span className="text-xs font-mono text-[#FF3B30] uppercase tracking-[0.2em]">The AYO Difference</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-white uppercase mt-3">
            Overlay Ads Are Dead.
          </h2>
          <p className="text-gray-500 max-w-xl mt-3">Source-level delivery changes everything. Here's why brands pay 5-10x more for AYO placements.</p>
        </div>
        <ComparisonSection />
      </section>

      {/* SOURCE VISUAL */}
      <section className="max-w-6xl mx-auto px-6 py-8">
        <div className="relative rounded-xl overflow-hidden border border-white/10">
          <img
            src="https://static.prod-images.emergentagent.com/jobs/34cab473-7882-4a8a-abac-c36cae9e6ad7/images/dd3361aeac50da95ed7f4119e607aa385680b41b18cddde007a0e9d8b158de66.png"
            alt="OBS Source Level Integration"
            className="w-full h-[350px] object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/60 to-transparent" />
          <div className="absolute bottom-8 left-8 max-w-md">
            <div className="flex items-center gap-2 mb-3">
              <Signal className="w-4 h-4 text-[#FF3B30]" />
              <span className="text-xs font-mono text-[#FF3B30] uppercase tracking-widest">Source Integration</span>
            </div>
            <p className="text-xl font-heading font-bold text-white mb-2">The ad IS the stream.</p>
            <p className="text-sm text-gray-400">When a creator triggers a commercial, the ad plays through OBS as the primary source. Every viewer on every platform sees the exact same full-screen, full-audio ad experience.</p>
          </div>
        </div>
      </section>

      {/* EARNINGS CALCULATOR */}
      <section id="calculator" className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-left mb-10">
          <span className="text-xs font-mono text-emerald-400 uppercase tracking-[0.2em]">Creator Earnings</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-white uppercase mt-3">
            See What You'd Earn
          </h2>
          <p className="text-gray-500 max-w-xl mt-3">Drag the sliders. No signup required. Real CPM tiers based on our marketplace.</p>
        </div>
        <EarningsCalculator />
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-white uppercase mb-3">How It Works</h2>
          </div>

          <div className="flex justify-center mb-10">
            <div className="inline-flex border border-white/10 rounded-none overflow-hidden">
              <button data-testid="toggle-brands" onClick={() => setActiveTab('brands')}
                className={`px-8 py-3 text-xs font-bold uppercase tracking-widest transition-all ${activeTab === 'brands' ? 'bg-[#7C3AED] text-white' : 'text-gray-500 hover:text-white bg-transparent'}`}>
                For Brands
              </button>
              <button data-testid="toggle-creators" onClick={() => setActiveTab('creators')}
                className={`px-8 py-3 text-xs font-bold uppercase tracking-widest transition-all ${activeTab === 'creators' ? 'bg-[#FF3B30] text-white' : 'text-gray-500 hover:text-white bg-transparent'}`}>
                For Creators
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
            {(activeTab === 'brands' ? [
              { step: '01', title: 'Upload Creative', desc: 'Upload video or image ads. Admin approves within minutes.' },
              { step: '02', title: 'Browse Marketplace', desc: 'Filter streamers by CCV, genre, platform, demographics.' },
              { step: '03', title: 'Place Your Bid', desc: 'Set CPM bid above floor. Admin approves the placement.' },
              { step: '04', title: 'Go Live', desc: 'Ads play at source level across all platforms simultaneously.' },
            ] : [
              { step: '01', title: 'Create Profile', desc: 'Add your stats, platforms, categories for brand discovery.' },
              { step: '02', title: 'Get Bids', desc: 'Brands compete to bid on your audience. Higher CCV = higher CPM.' },
              { step: '03', title: 'Go Commercial', desc: 'One button. Cut to ad during natural breaks in your stream.' },
              { step: '04', title: 'Get Paid', desc: '70% revenue share. Real-time tracking. Instant payouts.' },
            ]).map((item, i) => (
              <div key={i} className={`p-8 border-r border-white/5 last:border-r-0 ${i === 0 ? '' : ''} relative group`}>
                <span className={`text-5xl font-black font-mono ${activeTab === 'brands' ? 'text-[#7C3AED]/10' : 'text-[#FF3B30]/10'} block mb-4`}>{item.step}</span>
                <h3 className="font-heading text-base font-bold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES BENTO */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="text-left mb-10">
          <span className="text-xs font-mono text-gray-500 uppercase tracking-[0.2em]">Platform</span>
          <h2 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter text-white uppercase mt-3">Built for Live</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { icon: Monitor, title: 'OBS Native', desc: 'Browser source integration. Zero latency. No plugins required.', span: '' },
            { icon: Globe, title: 'Multi-Platform', desc: 'One trigger reaches every platform your stream is live on.', span: '' },
            { icon: BarChart3, title: 'CPM Marketplace', desc: 'Transparent bidding. Floor CPMs by viewer count. Brands compete for your audience.', span: 'md:col-span-1' },
            { icon: Zap, title: 'WebSocket Powered', desc: 'Sub-second trigger delivery. Real-time connection status.', span: '' },
            { icon: Shield, title: 'Admin Reviewed', desc: 'Every ad and placement approved before going live.', span: '' },
            { icon: TrendingUp, title: 'Live Earnings', desc: 'Per-impression tracking. Instant dashboard updates.', span: '' },
          ].map((f, i) => (
            <div key={i} className={`border border-white/[0.06] rounded-lg bg-[#0a0a0a] p-6 hover:border-white/10 transition-all group ${f.span}`}>
              <f.icon className="w-5 h-5 text-[#FF3B30]/70 mb-4 group-hover:text-[#FF3B30] transition-colors" />
              <h3 className="font-heading text-sm font-bold text-white mb-1.5">{f.title}</h3>
              <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter text-white uppercase mb-4">
          Ready to Go Live?
        </h2>
        <p className="text-gray-500 max-w-lg mx-auto mb-10">Join the first source-level ad network for live streaming. Whether you're buying reach or monetizing your audience.</p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link to="/signup/brand" data-testid="final-cta-brand">
            <Button className="bg-[#7C3AED] hover:bg-[#6D28D9] text-white font-bold uppercase tracking-widest py-5 px-10 rounded-none text-xs h-auto">
              Start Advertising <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
          <Link to="/signup/creator" data-testid="final-cta-creator">
            <Button className="bg-[#FF3B30] hover:bg-[#D70015] text-white font-bold uppercase tracking-widest py-5 px-10 rounded-none text-xs h-auto">
              Start Earning <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-white/5 bg-[#0a0a0a]">
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-7 h-7 bg-[#FF3B30] flex items-center justify-center"><Radio className="w-3.5 h-3.5 text-white" /></div>
              <span className="font-heading text-lg font-black tracking-tighter text-white uppercase">AYO<span className="text-gray-500 ml-1 font-medium text-sm normal-case">source</span></span>
            </div>
            <div className="flex items-center gap-8">
              <Link to="/signup/brand" className="text-xs text-gray-500 hover:text-white transition-colors font-mono uppercase tracking-wider">Brands</Link>
              <Link to="/signup/creator" className="text-xs text-gray-500 hover:text-white transition-colors font-mono uppercase tracking-wider">Creators</Link>
              <Link to="/login" className="text-xs text-gray-500 hover:text-white transition-colors font-mono uppercase tracking-wider">Sign In</Link>
            </div>
            <p className="text-[10px] text-gray-700 font-mono uppercase tracking-widest">Source-Level Ad Delivery</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
